import { match } from 'ts-pattern';
import { z } from 'zod';

import { B2bContractFeeLabel } from '@Common/types/bo/b2b/contract_fee_charges';
import { B2bPlanFeePaidBy, B2bPlanFeePaidByValues } from '@Common/types/bo/b2b/plans';

export const ZodB2bContractCustodyFees = z.discriminatedUnion('custody_config', [
  z.object({
    custody_config: z.literal('standard'),
    low_workforce_low_amount: z.number().int(),
    low_workforce_high_amount: z.number().int(),
    high_workforce_low_amount: z.number().int(),
    high_workforce_high_amount: z.number().int(),
    paid_by_for_current_employees: z.enum(B2bPlanFeePaidByValues),
    paid_by_for_past_employees: z.enum(B2bPlanFeePaidByValues),
    paid_by_for_retired_employees: z.enum(B2bPlanFeePaidByValues),
  }),
  z.object({
    custody_config: z.literal('custom_all_plans'),
    amount_for_current_employees: z.number().int(),
    amount_for_past_employees: z.number().int(),
    amount_for_retired_employees: z.number().int(),
    paid_by_for_current_employees: z.enum(B2bPlanFeePaidByValues),
    paid_by_for_past_employees: z.enum(B2bPlanFeePaidByValues),
    paid_by_for_retired_employees: z.enum(B2bPlanFeePaidByValues),
  }),
  z.object({
    custody_config: z.literal('custom_per_plan'),
    amount_for_current_employees_pee: z.number().int(),
    amount_for_past_employees_pee: z.number().int(),
    amount_for_retired_employees_pee: z.number().int(),
    amount_for_current_employees_pereco: z.number().int(),
    amount_for_past_employees_pereco: z.number().int(),
    amount_for_retired_employees_pereco: z.number().int(),
    paid_by_for_current_employees_pee: z.enum(B2bPlanFeePaidByValues),
    paid_by_for_past_employees_pee: z.enum(B2bPlanFeePaidByValues),
    paid_by_for_retired_employees_pee: z.enum(B2bPlanFeePaidByValues),
    paid_by_for_current_employees_pereco: z.enum(B2bPlanFeePaidByValues),
    paid_by_for_past_employees_pereco: z.enum(B2bPlanFeePaidByValues),
    paid_by_for_retired_employees_pereco: z.enum(B2bPlanFeePaidByValues),
  }),
  z.object({
    custody_config: z.literal('custom_flat_rate'),
    flat_rate_amount_pee_pereco: z.number().int(),
    paid_by_for_current_employees: z.enum(B2bPlanFeePaidByValues),
    paid_by_for_past_employees: z.enum(B2bPlanFeePaidByValues),
    paid_by_for_retired_employees: z.enum(B2bPlanFeePaidByValues),
  }),
  z.object({
    custody_config: z.literal('custom_old_pricing'),
    base_fees_amount: z.number().int(),
    base_flat_rate_amount: z.number().int(),
    paid_by_for_current_employees: z.enum(B2bPlanFeePaidByValues),
    paid_by_for_past_employees: z.enum(B2bPlanFeePaidByValues),
    paid_by_for_retired_employees: z.enum(B2bPlanFeePaidByValues),
  }),
]);

export const ZodB2bContractFees = z.object({
  custody: ZodB2bContractCustodyFees,
  company: z.object({
    file_opening: z.number().int(),
    calculating_individual_allocations: z.number().int(),
    integration_of_beneficiary_data: z.number().int(),
    integration_of_affiliates_choices_via_mail: z.number().int(),
    integration_of_affiliates_consultation_data: z.number().int(),
    rejection_of_direct_debit_or_bank_transfer: z.number().int(),
    extra_reminder_for_unpaid_invoices: z.number().int(),
    collective_transfer_to_another_manager: z.number().int(),
  }),
  affiliate: z.object({
    statements_and_certificates: z.number().int(),
    annual_custody_for_former_or_retired_employees: z.number().int(),
    annual_custody_pereco_pct: z.number(),
    annual_custody_pereco_max: z.number().int(),
    account_position_at_given_date: z.number().int(),
    undelivered_mail_handling: z.number().int(),
    pledging_or_release_of_assets: z.number().int(),
    search_for_documents_or_transactions_before_10_years: z.number().int(),
    search_for_documents_or_transactions_after_10_years: z.number().int(),
    transfer_from_pee_to_another_manager: z.number().int(),
    transfer_from_pereco_to_another_manager_pct: z.number(),
    estate_settlement_low_amount: z.number().int(),
    estate_settlement_high_amount: z.number().int(),
    account_closure: z.number().int(),
    specific_mail_requests_processing: z.number().int(),
    caisse_des_depots_recording: z.number().int(),
    voluntary_payment_by_cheque: z.number().int(),
    repayment_of_savings: z.number().int(),
    retractation_following_pereco_payment: z.number().int(),
    direct_debit_rejection: z.number().int(),
    unpaid_cheques: z.number().int(),
    repayment_reimbursement_triggered: z.number().int(),
    repayment_by_cheque: z.number().int(),
    repayment_by_bank_transfer: z.number().int(),
    repayment_settlement_reissuing: z.number().int(),
  }),
});
export type B2bContractFees = z.TypeOf<typeof ZodB2bContractFees>;
export const getB2bContractFeeByLabel = (fees: B2bContractFees, label: B2bContractFeeLabel) =>
  match(label)
    .with(
      'company_rejection_of_direct_debit_or_bank_transfer',
      () => fees.company.rejection_of_direct_debit_or_bank_transfer,
    )
    .with(
      'company_collective_transfer_to_another_manager',
      () => fees.company.collective_transfer_to_another_manager,
    )
    .with('company_file_opening', () => fees.company.file_opening)
    .with('company_pee_pereco_annual_fee', () =>
      fees.custody.custody_config === 'custom_flat_rate'
        ? fees.custody.flat_rate_amount_pee_pereco
        : null,
    )
    .with(
      'company_calculating_individual_allocations_particip',
      () => fees.company.calculating_individual_allocations,
    )
    .with(
      'company_calculating_individual_allocations_profit',
      () => fees.company.calculating_individual_allocations,
    )
    .with(
      'company_integration_of_affiliates_consultation_data_particip',
      () => fees.company.integration_of_affiliates_consultation_data,
    )
    .with(
      'company_integration_of_affiliates_consultation_data_profit',
      () => fees.company.integration_of_affiliates_consultation_data,
    )
    .with(
      'company_integration_of_affiliates_consultation_data_value',
      () => fees.company.integration_of_affiliates_consultation_data,
    )
    .with(
      'company_integration_of_affiliates_choices_via_mail_particip',
      () => fees.company.integration_of_affiliates_choices_via_mail,
    )
    .with(
      'company_integration_of_affiliates_choices_via_mail_profit',
      () => fees.company.integration_of_affiliates_choices_via_mail,
    )
    .with(
      'company_integration_of_affiliates_choices_via_mail_value',
      () => fees.company.integration_of_affiliates_choices_via_mail,
    )
    .with('affiliate_account_closure', () => fees.affiliate.account_closure)
    .with(
      'affiliate_account_position_at_given_date',
      () => fees.affiliate.account_position_at_given_date,
    )
    .with(
      'affiliate_annual_custody_for_former_or_retired_employees',
      () => fees.affiliate.annual_custody_for_former_or_retired_employees,
    )
    .with('affiliate_annual_custody_for_former_or_retired_employees_pereco', () => null)
    .with('affiliate_repayment_by_cheque', () => fees.affiliate.repayment_by_cheque)
    .with('affiliate_voluntary_payment_by_cheque', () => fees.affiliate.voluntary_payment_by_cheque)
    .with('affiliate_direct_debit_rejection', () => fees.affiliate.direct_debit_rejection)
    .with(
      'affiliate_estate_settlement_high_amount',
      () => fees.affiliate.estate_settlement_high_amount,
    )
    .with(
      'affiliate_estate_settlement_low_amount',
      () => fees.affiliate.estate_settlement_low_amount,
    )
    .with(
      'affiliate_pledging_or_release_of_assets',
      () => fees.affiliate.pledging_or_release_of_assets,
    )
    .with('affiliate_repayment_of_savings', () => fees.affiliate.repayment_of_savings)
    .with(
      'affiliate_repayment_reimbursement_triggered',
      () => fees.affiliate.repayment_reimbursement_triggered,
    )
    .with(
      'affiliate_retractation_following_pereco_payment',
      () => fees.affiliate.retractation_following_pereco_payment,
    )
    .with(
      'affiliate_search_for_documents_or_transactions_after_10_years',
      () => fees.affiliate.search_for_documents_or_transactions_after_10_years,
    )
    .with(
      'affiliate_search_for_documents_or_transactions_before_10_years',
      () => fees.affiliate.search_for_documents_or_transactions_before_10_years,
    )
    .with(
      'affiliate_specific_mail_requests_processing',
      () => fees.affiliate.specific_mail_requests_processing,
    )
    .with('affiliate_statements_and_certificates', () => fees.affiliate.statements_and_certificates)
    .with(
      'affiliate_transfer_from_pee_to_another_manager',
      () => fees.affiliate.transfer_from_pee_to_another_manager,
    )
    .with(
      'affiliate_transfer_from_pereco_to_another_manager_pct',
      () => fees.affiliate.transfer_from_pereco_to_another_manager_pct,
    )
    .with('affiliate_undelivered_mail_handling', () => fees.affiliate.undelivered_mail_handling)
    .with(
      'company_integration_of_affiliates_data',
      () => fees.company.integration_of_affiliates_consultation_data,
    )
    .with('affiliate_reissue_bank_transfer', () => fees.affiliate.repayment_settlement_reissuing)
    .with(
      'affiliate_collective_operation_by_cheque',
      () => fees.affiliate.voluntary_payment_by_cheque,
    )
    .otherwise(() => null);

export function computePerecoCustodyFee({
  aumCents,
  ratePct,
  maxCents,
}: {
  aumCents: number;
  ratePct: number;
  maxCents: number;
}): number {
  if (aumCents <= 0 || ratePct <= 0 || maxCents <= 0) {
    return 0;
  }

  return Math.min(Math.round((aumCents * ratePct) / 100), maxCents);
}

type B2bContractsFeesDefaultConfig<T> = T extends number
  ? { defaultValue: number }
  : T extends B2bPlanFeePaidBy
    ? { defaultValue: B2bPlanFeePaidBy }
    : never;

type B2bContractCustodyStandard = Extract<
  z.infer<typeof ZodB2bContractCustodyFees>,
  { custody_config: 'standard' }
>;

export const b2bContractsFeesDefaultConfig: {
  custody: {
    [K in keyof Omit<B2bContractCustodyStandard, 'custody_config'>]: B2bContractsFeesDefaultConfig<
      B2bContractCustodyStandard[K]
    >;
  };
  company: {
    [K in keyof B2bContractFees['company']]: B2bContractsFeesDefaultConfig<
      B2bContractFees['company'][K]
    >;
  };
  affiliate: {
    [K in keyof B2bContractFees['affiliate']]: B2bContractsFeesDefaultConfig<
      B2bContractFees['affiliate'][K]
    >;
  };
} = {
  custody: {
    low_workforce_low_amount: { defaultValue: 199_00 },
    low_workforce_high_amount: { defaultValue: 99_00 },
    high_workforce_low_amount: { defaultValue: 14_00 },
    high_workforce_high_amount: { defaultValue: 7_00 },
    paid_by_for_current_employees: { defaultValue: 'company' },
    paid_by_for_past_employees: { defaultValue: 'employee' },
    paid_by_for_retired_employees: { defaultValue: 'employee' },
  },
  company: {
    file_opening: { defaultValue: 0 },
    calculating_individual_allocations: { defaultValue: 276_00 },
    integration_of_beneficiary_data: { defaultValue: 411_00 },
    integration_of_affiliates_choices_via_mail: { defaultValue: 2_00 },
    integration_of_affiliates_consultation_data: { defaultValue: 420_00 },
    rejection_of_direct_debit_or_bank_transfer: { defaultValue: 23_00 },
    extra_reminder_for_unpaid_invoices: { defaultValue: 40_00 },
    collective_transfer_to_another_manager: { defaultValue: 23_00 },
  },
  affiliate: {
    statements_and_certificates: { defaultValue: 2_40 },
    annual_custody_for_former_or_retired_employees: { defaultValue: 39_20 },
    annual_custody_pereco_pct: { defaultValue: 5 },
    annual_custody_pereco_max: { defaultValue: 20_00 },
    account_position_at_given_date: { defaultValue: 63_90 },
    undelivered_mail_handling: { defaultValue: 29_10 },
    pledging_or_release_of_assets: { defaultValue: 53_40 },
    search_for_documents_or_transactions_before_10_years: {
      defaultValue: 65_20,
    },
    search_for_documents_or_transactions_after_10_years: {
      defaultValue: 99_80,
    },
    transfer_from_pee_to_another_manager: { defaultValue: 53_40 },
    transfer_from_pereco_to_another_manager_pct: { defaultValue: 1 },
    estate_settlement_low_amount: { defaultValue: 99_80 },
    estate_settlement_high_amount: { defaultValue: 378_00 },
    account_closure: { defaultValue: 41_20 },
    specific_mail_requests_processing: { defaultValue: 58_10 },
    caisse_des_depots_recording: { defaultValue: 0 },
    voluntary_payment_by_cheque: { defaultValue: 12_70 },
    repayment_of_savings: { defaultValue: 24_80 },
    retractation_following_pereco_payment: { defaultValue: 29_10 },
    direct_debit_rejection: { defaultValue: 23_30 },
    unpaid_cheques: { defaultValue: 22_80 },
    repayment_reimbursement_triggered: { defaultValue: 24_80 },
    repayment_by_cheque: { defaultValue: 12_70 },
    repayment_by_bank_transfer: { defaultValue: 0 },
    repayment_settlement_reissuing: { defaultValue: 36_10 },
  },
};
